import { AccredibleButtonComponentModule } from '@accredible-frontend-v2/new-components/button';
import { AccredibleChipComponentModule } from '@accredible-frontend-v2/new-components/chip';
import { AccredibleInputFeedbackModule } from '@accredible-frontend-v2/new-components/input-feedback';
import {
  AccredibleSelectInputComponentModule,
  SelectInputOption,
} from '@accredible-frontend-v2/new-components/select-input';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import {
  AccredibleResponsiveBreakpoint,
  AccredibleResponsiveHelper,
} from '@accredible-frontend-v2/utils/responsive-helper';
import { CommonModule } from '@angular/common';
import { Component, computed, forwardRef, inject, input, signal } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { Observable } from 'rxjs';

export interface DynamicTableField {
  key: string;
  label: Observable<string>;
  type: 'text' | 'number' | 'date' | 'select';
  placeholder?: Observable<string>;
  width: number;
  validators?: ValidatorFn[];
  options?: SelectInputOption[];
}

export interface DynamicTableRow {
  [key: string]: string | number | Date;
}

@Component({
  selector: 'accredible-dynamic-table',
  standalone: true,
  imports: [
    CommonModule,
    AccredibleChipComponentModule,
    TranslocoModule,
    ReactiveFormsModule,
    AccredibleButtonComponentModule,
    AccredibleInputFeedbackModule,
    AccredibleSelectInputComponentModule,
  ],
  templateUrl: './dynamic-table.component.html',
  styleUrls: ['./dynamic-table.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DynamicTableComponent),
      multi: true,
    },
  ],
})
export class DynamicTableComponent implements ControlValueAccessor {
  private readonly _language = inject(AccredibleLanguageService);
  fields = input<DynamicTableField[]>([]);
  addButtonText = input<string>(this._language.translate('dynamic-table.add_row'));
  addAdditionalButtonText = input<string>(this._language.translate('dynamic-table.add_additional'));
  editFirstRowByDefault = input<boolean>(false);
  maxItems = input<number | undefined>(undefined);

  form = signal<FormGroup>(null);
  editingIndex = signal<number | null>(null);
  pendingRow = signal<FormGroup | null>(null);

  rows = computed(() => this.form().get('rows') as FormArray);
  canAddRow = computed(() => {
    const hasMaxItems = this.maxItems() !== undefined && this.rows().length >= this.maxItems()!;
    return this.editingIndex() === null && !this.pendingRow() && !hasMaxItems;
  });

  onChange: (value: DynamicTableRow[]) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private _fb: FormBuilder) {
    this.form.set(
      this._fb.group({
        rows: this._fb.array([]),
      }),
    );
  }

  writeValue(value: DynamicTableRow[]): void {
    const rows = this.rows();
    rows.clear();
    this.editingIndex.set(null);

    if (value && value.length > 0) {
      value.forEach((rowData) => {
        const row = this._createPendingRow();
        row.patchValue(rowData);
        rows.push(row);
      });
    } else if (this.editFirstRowByDefault()) {
      const row = this._createPendingRow();
      row.patchValue({});
      rows.push(row);
      this.editingIndex.set(0);
    }
  }

  registerOnChange(fn: (value: DynamicTableRow[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  addRow(): void {
    if (this.pendingRow()) {
      return;
    }

    this.pendingRow.set(this._createPendingRow());
    this.editingIndex.set(this.rows().length);
  }

  removeRow(index: number): void {
    this.editingIndex.set(null);

    if (index === this.rows().length && this.pendingRow()) {
      this.pendingRow.set(null);
    } else {
      this.rows().removeAt(index);
    }

    // If editFirstRowByDefault is enabled and all rows are deleted, add a new empty editable row
    if (this.editFirstRowByDefault() && this.rows().length === 0) {
      const row = this._createPendingRow();
      row.patchValue({});
      this.rows().push(row);
      this.editingIndex.set(0);
      this.onChange([]);
    } else {
      this.onChange(this.rows().value);
    }
  }

  saveRow(index: number): void {
    if (!this.isRowValid(index)) {
      return;
    }
    if (index === this.rows().length && this.pendingRow()) {
      this.rows().push(this.pendingRow()!);
      this.pendingRow.set(null);
    }
    this.editingIndex.set(null);
    this.onChange(this.rows().value);
    this.onTouched();
  }

  editRow(index: number): void {
    if (this.pendingRow() && index !== this.rows().length) {
      this.pendingRow.set(null);
      this.editingIndex.set(null);
    }
    this.editingIndex.set(index);
  }

  isRowValid(index: number): boolean {
    const row = index === this.rows().length ? this.pendingRow() : this.rows().at(index);
    return row ? row.valid : false;
  }

  getErrorMessage(control: AbstractControl): string | null {
    if (!control?.errors) return null;

    if (!!control.errors['required']) {
      return this._language.translate('dynamic-table.errors.required');
    }
    if (!!control.errors['email']) {
      return this._language.translate('dynamic-table.errors.email');
    }

    if (!!control.errors['max']) {
      return this._language.translate('dynamic-table.errors.max', {
        max: control.errors['max'].max,
      });
    }

    return null;
  }

  getFormControl(control: AbstractControl | null): FormControl {
    return control as FormControl;
  }

  getSelectSize(): 'small' | 'normal' {
    return AccredibleResponsiveHelper.isScreenLessThan(AccredibleResponsiveBreakpoint.SM)
      ? 'normal'
      : 'small';
  }

  getDisplayValue(field: DynamicTableField, value: string | number | Date): string {
    if (field.type === 'select' && field.options) {
      const option = field.options.find((opt) => opt['value'] === value);
      return option?.['name'] || String(value);
    }
    return String(value);
  }

  private _createPendingRow(): FormGroup {
    const row = this._fb.group({});
    this.fields().forEach((field) => {
      const validators = field.validators || [];
      row.addControl(field.key, this._fb.control('', [Validators.required, ...validators]));
    });
    return row;
  }
}
